import type { AccessLevel, PersonLevel } from "../../computers/people"
import type { SegmentedOption } from "./Segmented"

/** Levels a person can be given on a project, in the order they escalate. */
export const SHARE_LEVELS: readonly SegmentedOption<Exclude<AccessLevel, "admin">>[] = [
    { id: "view", label: "View", hint: "Reads threads, diffs, and files. Nothing runs." },
    { id: "propose", label: "Propose", hint: "Everything in View, plus suggestions you approve before they run." },
    { id: "operate", label: "Operate", hint: "Runs agents and commands in this project on your computer, as you." },
]

export const ACCESS_LEVELS_WITH_ADMIN: readonly SegmentedOption<AccessLevel>[] = [
    ...SHARE_LEVELS,
    { id: "admin", label: "Admin", hint: "Everything on this computer, exactly like you. For your own devices." },
]

export const DURATIONS: ReadonlyArray<{ id: string; label: string; ms: number | null }> = [
    { id: "forever", label: "Until you change it", ms: null },
    { id: "2h", label: "For 2 hours", ms: 2 * 60 * 60 * 1000 },
    { id: "1d", label: "For a day", ms: 24 * 60 * 60 * 1000 },
    { id: "1w", label: "For a week", ms: 7 * 24 * 60 * 60 * 1000 },
]

export function durationExpiry(id: string, now = Date.now()): string | undefined {
    const chosen = DURATIONS.find((candidate) => candidate.id === id)
    return chosen?.ms ? new Date(now + chosen.ms).toISOString() : undefined
}

export function levelWord(level: PersonLevel): string {
    switch (level) {
        case "view":
            return "View"
        case "propose":
            return "Propose"
        case "operate":
            return "Operate"
        case "admin":
            return "Admin"
        default:
            return "No access"
    }
}

/** "until Sep 9, 4:12 PM" or nothing when a grant does not expire. */
export function untilLabel(expiresAt: string | undefined): string {
    if (!expiresAt) return ""
    const date = new Date(expiresAt)
    if (Number.isNaN(date.getTime())) return ""
    return `until ${date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
}

export function remainingLabel(expiresAt: string, now: number): string {
    const seconds = Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / 1000))
    if (seconds === 0) return "Expired"
    const minutes = Math.floor(seconds / 60)
    const remainder = seconds % 60
    return `${minutes}:${remainder.toString().padStart(2, "0")} left`
}

/** "30 minutes", "1 minute", or "under a minute", for prose that should not tick every second. */
export function expiresInWords(expiresAt: string, now: number): string {
    const minutes = Math.ceil((new Date(expiresAt).getTime() - now) / 60_000)
    if (!Number.isFinite(minutes) || minutes <= 0) return "under a minute"
    return `${minutes} minute${minutes === 1 ? "" : "s"}`
}

export function joinNames(names: readonly string[]): string {
    if (names.length === 0) return ""
    if (names.length === 1) return names[0] as string
    if (names.length === 2) return `${names[0]} and ${names[1]}`
    return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`
}
